import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';
import DataGrid from '../../components/Common/DataGrid';
import { dateTime, longDate, rs, shortDate } from '../format';
import { Async, BucketPill, Button, FilterPills, PageHeader, Pagination, Panel, Timeline } from '../ui';
import useFetch from '../useFetch';
import { ProofCell } from '../Proof';
import { PayoutModal } from './AdminModals';

const BUCKETS = ['Approved', 'Pending', 'In progress', 'Declined'];

/** Reseller name over their subdomain, the same cell on every admin table. */
const Who = ({ name, subdomain }) => <><strong>{name}</strong><small>{subdomain}</small></>;
const more = (d) => d.total > d.items.length && <p className="ds-foot">Showing the newest {d.items.length} of {d.total}.</p>;

export const Commissions = () => {
  const [bucket, setBucket] = useState('');
  const state = useFetch('/admin/commissions', { limit: 1000, ...(bucket ? { bucket } : {}) });
  const columns = [
    { title: 'Date', render: (a) => shortDate(a.date), value: (a) => a.date || '' },
    { title: 'Reseller', render: (a) => <Who name={a.reseller_name} subdomain={a.subdomain} />, value: (a) => `${a.reseller_name} ${a.subdomain}` },
    { title: 'Product', render: (a) => a.product, value: (a) => a.product },
    { title: 'Application', render: (a) => <span style={{ fontSize: 12 }}>{a.application_id || '–'}</span>, value: (a) => a.application_id || '' },
    { title: 'Status', render: (a) => <BucketPill bucket={a.bucket} label={a.status} />, value: (a) => a.status },
    { title: 'Commission', numeric: true, render: (a) => (a.commission > 0 ? rs(a.commission) : '–'), value: (a) => a.commission },
  ];
  return (
    <>
      <PageHeader eyebrow="Manage" title="Commissions" />
      <Panel title="Commission per application" subtitle="Commission is earned the first time an application is approved, at the reseller's rate that day.">
        <div style={{ marginBottom: 14 }}>
          <FilterPills label="Filter by status" value={bucket} onChange={setBucket} options={[{ value: '', label: 'All' }, ...BUCKETS.map((b) => ({ value: b, label: b }))]} />
        </div>
        <Async state={state}>
          {(d) => (
            <>
              <DataGrid rows={d.items} columns={columns} order={[[0, 'desc']]} emptyText="No commissions yet. They appear when finOS reports an application for one of your resellers." />
              {more(d)}
            </>
          )}
        </Async>
      </Panel>
    </>
  );
};

export const Payouts = () => {
  const [paying, setPaying] = useState(false);
  const [choices, setChoices] = useState([]);
  const state = useFetch('/admin/payouts', { limit: 1000 });

  const openPay = async () => {
    const { data } = await apiClient.get('/admin/resellers', { params: { limit: 1000 } });
    setChoices(data.items.filter((r) => r.commission_balance > 0));
    setPaying(true);
  };

  const columns = [
    { title: 'Date', render: (p) => longDate(p.paid_at), value: (p) => p.paid_at || '' },
    { title: 'Reseller', render: (p) => <Who name={p.reseller_name} subdomain={p.subdomain} />, value: (p) => `${p.reseller_name} ${p.subdomain}` },
    { title: 'Reference', render: (p) => p.reference || '–', value: (p) => p.reference || '' },
    { title: 'Note', render: (p) => p.note || '–', value: (p) => p.note || '' },
    { title: 'Proof of payment', render: (p) => <ProofCell payout={p} base="/admin" canUpload onChanged={state.reload} />, value: (p) => p.proof_name || '' },
    { title: 'Recorded by', render: (p) => <span style={{ fontSize: 12 }}>{p.paid_by || '–'}</span>, value: (p) => p.paid_by || '' },
    { title: 'Amount', numeric: true, render: (p) => <strong>{rs(p.amount)}</strong>, value: (p) => p.amount },
  ];

  return (
    <>
      <PageHeader eyebrow="Manage" title="Payouts">
        <Button variant="primary" onClick={openPay}>Record payout</Button>
      </PageHeader>
      <Panel title="Payments recorded" subtitle="Money you have already sent to resellers. A payout can never be more than the balance owed.">
        <Async state={state}>
          {(d) => (
            <>
              <DataGrid rows={d.items} columns={columns} order={[[0, 'desc']]} emptyText="No payouts recorded. Use Record payout after you pay a reseller from your bank." />
              {more(d)}
            </>
          )}
        </Async>
      </Panel>
      <PayoutModal open={paying} reseller={null} choices={choices} onClose={() => setPaying(false)} onDone={state.reload} />
      {paying && choices.length === 0 && (
        <p className="ds-foot">Every reseller is paid up, so there is nobody to record a payout for. <Link to="/admin/resellers">See resellers</Link></p>
      )}
    </>
  );
};

export const Customers = () => {
  const state = useFetch('/admin/customers', { limit: 1000 });
  const columns = [
    { title: 'Date', render: (c) => shortDate(c.date), value: (c) => c.date || '' },
    { title: 'Customer', render: (c) => <><strong>{c.name}</strong><small>{c.email}</small></>, value: (c) => `${c.name} ${c.email}` },
    { title: 'Product', render: (c) => <>{c.product}<small>{c.application_id}</small></>, value: (c) => `${c.product} ${c.application_id || ''}` },
    { title: 'Reseller', render: (c) => <Who name={c.reseller_name} subdomain={c.subdomain} />, value: (c) => `${c.reseller_name} ${c.subdomain}` },
    { title: 'Status', render: (c) => <BucketPill bucket={c.bucket} label={c.status} />, value: (c) => c.status },
  ];
  return (
    <>
      <PageHeader eyebrow="Manage" title="Customers" />
      <Panel title="Applications sent by resellers">
        <Async state={state}>
          {(d) => (
            <>
              <DataGrid rows={d.items} columns={columns} order={[[0, 'desc']]} emptyText="No customers yet. They appear when finOS reports an application from a partner site." />
              {more(d)}
            </>
          )}
        </Async>
      </Panel>
    </>
  );
};

const EVENTS_PER_PAGE = 10;

export const Activity = () => {
  const state = useFetch('/admin/activity', { limit: 100 });
  const [page, setPage] = useState(1);
  return (
    <>
      <PageHeader eyebrow="Manage" title="Activity" />
      <Panel title="Latest events" subtitle="Commissions, application status changes, payouts and signups. The newest 100 are kept here.">
        <Async state={state}>
          {(events) => {
            const pages = Math.max(1, Math.ceil(events.length / EVENTS_PER_PAGE));
            const current = Math.min(page, pages);
            const shown = events.slice((current - 1) * EVENTS_PER_PAGE, current * EVENTS_PER_PAGE);
            return (
              <>
                <Timeline events={shown.map((e) => ({ ...e, when: dateTime(e.at) }))} empty="Events appear here as the platform is used." />
                <Pagination total={events.length} pageSize={EVENTS_PER_PAGE} page={current} onChange={setPage} />
              </>
            );
          }}
        </Async>
      </Panel>
    </>
  );
};
